import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { costsApi } from '../api/costs';
import Card from './Card';
import LoadingSpinner from './LoadingSpinner';

export default function UserUsageTab() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['costs-by-user'],
    queryFn: () => costsApi.getByUser(),
  });

  const rows = data?.data ?? [];

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          chapters: acc.chapters + r.chapters,
          cost_usd: acc.cost_usd + r.cost_usd,
          tokens: acc.tokens + r.tokens,
        }),
        { chapters: 0, cost_usd: 0, tokens: 0 }
      ),
    [rows]
  );

  if (isLoading) return <LoadingSpinner />;
  if (isError) {
    return (
      <Card>
        <p className="text-red-600 dark:text-red-400">Не удалось загрузить статистику по пользователям.</p>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100">
        Учёт по пользователям
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Сколько глав перевёл и сколько потратил каждый пользователь (по завершённым переводам).
      </p>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              <th className="py-2 pr-4 font-medium">Пользователь</th>
              <th className="py-2 px-4 font-medium">Роль</th>
              <th className="py-2 px-4 font-medium text-right">Глав переведено</th>
              <th className="py-2 px-4 font-medium text-right">Потрачено, $</th>
              <th className="py-2 pl-4 font-medium text-right">Токены</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-gray-500 dark:text-gray-400">
                  Нет данных
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.user_id}
                className="border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100"
              >
                <td className="py-2 pr-4">
                  <div className="font-medium">{r.username || r.email}</div>
                  {r.username && <div className="text-xs text-gray-500 dark:text-gray-400">{r.email}</div>}
                </td>
                <td className="py-2 px-4">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-xs ${
                      r.role === 'admin'
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                    }`}
                  >
                    {r.role}
                  </span>
                </td>
                <td className="py-2 px-4 text-right tabular-nums">{r.chapters.toLocaleString('ru-RU')}</td>
                <td className="py-2 px-4 text-right tabular-nums">${r.cost_usd.toFixed(2)}</td>
                <td className="py-2 pl-4 text-right tabular-nums">{r.tokens.toLocaleString('ru-RU')}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="font-semibold text-gray-900 dark:text-gray-100 border-t-2 border-gray-200 dark:border-gray-700">
                <td className="py-2 pr-4">Итого</td>
                <td className="py-2 px-4"></td>
                <td className="py-2 px-4 text-right tabular-nums">{totals.chapters.toLocaleString('ru-RU')}</td>
                <td className="py-2 px-4 text-right tabular-nums">${totals.cost_usd.toFixed(2)}</td>
                <td className="py-2 pl-4 text-right tabular-nums">{totals.tokens.toLocaleString('ru-RU')}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}
